/**
 * @class that creates an Event Delegator that listens for events.
 * @version 1.2
 */
class EventDelegator
{
  /**
   * @param {Array} delegators - Array of delegators.
   */
  constructor( delegators = [ {} ] )
  {
    this.init( delegators );
  }

  init( delegators )
  {
    this.delegators = [];
    this.pollyfillIncludes();
    this.validateDelegators( delegators );
    this.addDelegators();
    this.dataset = {};
    this.mute = 'mute';
  }

  /**
   * @param {Object} delegator - A delegator object that contains a CSS selector, eventType and the events
   */
  validateDelegators( delegators )
  {
    if ( this.isArray( delegators ) )
    {
      delegators.forEach( ( delegator ) =>
      {
        this.validateDelegator( delegator );
      } );
    }
  }

  validateDelegator( delegator )
  {
    if ( this.isObject( delegator ) )
    {
      delegator = this.setupDelegator( Object.assign( {}, delegator ) );
      this.delegators.push( delegator );
    }
    else
    {
      this.throw( `${delegator} is not an object` );
    }
  }

  setupDelegator( delegator )
  {
    delegator.selector = this.getDelegator( delegator.selector );
    delegator.eventType = !delegator.eventType ? 'click' : delegator.eventType;

    if ( !delegator.events )
    {
      console.warn( `${delegator.selector} does not contain any events...` )
    }
    return delegator;
  }

  getDelegator( selector )
  {
    const element = document.querySelector( selector );
    return element ? element : this.throw( `Need a valid selector to delegate events - can't find ${selector} in the DOM` );
  }

  isArray( delegators )
  {
    return Array.isArray( delegators ) ? delegators : this.throw( 'Delegators need to be passed as an array of objects' );
  }

  isObject( delegator )
  {
    return delegator && typeof delegator === 'object' && delegator.constructor === Object;
  }

  /**
   * @param {string} delegator.selector - The selector for the parent HTML element that listens for all events.
   * @param {string} delegator.eventType - The event type, click, mouseout etc. Defaults to click.
   * @param {Object} delegator.events - An object where each key value pair is the target and callback. The key matches a data- attribute applied to a HTML element inside the delegator.
   */
  addDelegators()
  {
    const self = this;

    self.delegators.forEach( ( delegator ) =>
    {
      const { selector, eventType, events } = delegator;

      selector.addEventListener( eventType, ( e ) =>
      {
        let keys;
        keys = Object.keys( e.target.dataset );

        const callback_executed =  self.execute_callback( e, events, keys, self.update_dataset( e.target.dataset, true ) );

        if ( !keys.includes(this.mute) && !callback_executed )
        {
          self.update_dataset( e.target.dataset );

          const parents_event_keys = self.get_parent_event_keys( e.target, events );

          keys = self.update_event_keys( keys, parents_event_keys );

          self.execute_callback( e, events, keys, self.dataset );
        }
      } );
    } );
  }

  update_event_keys( event_keys, parents_event_keys )
  {

    if ( parents_event_keys.length )
    {
      for ( const key of parents_event_keys )
      {
        event_keys.push( key.key );
      }
    }
    return event_keys;
  }

  get_parent_event_keys( target, events )
  {
    let parent;
    let parents_event_keys;
    parent = target.parentElement;
    parents_event_keys = false;

    while ( parent && !parents_event_keys )
    {
      // find the closest parent that has a dataset key that matches the class events keys
      parents_event_keys = this.parents_event_keys( parent, events );
      parent = parent.parentElement;
    }
    return parents_event_keys;
  }

  parents_event_keys( parent, events )
  {
    const dataset_keys = Object.keys( parent.dataset );
    const dataset = parent.dataset;
    const keys = [];

    if ( dataset_keys )
    {
      for ( const key in events )
      {
        // match parents dataset keys to class events keys
        if ( dataset_keys.includes( key ) )
        {
          keys.push(
          {
            key: key
          } );
        }
      }
      this.update_dataset( dataset );
    }
    return keys.length ? keys : false;
  }

  update_dataset( dataset, reset )
  {
    if ( dataset )
    {
      if ( reset )
      {
        this.dataset = {};
      }

      for ( const prop in dataset )
      {
        this.dataset[ prop ] = dataset[ prop ];
      }
      return this.dataset;
    }
  }

  execute_callback( e, events, keys, dataset )
  {
    let callback_executed;
    callback_executed = false;
    for ( const key in events )
    {
      if ( keys.includes( key ) )
      {
        events[ key ]( e, dataset );
        callback_executed = true;
      }
    }
    return callback_executed;
  }

  throw ( message )
  {
    throw new Error( message );
  }

  pollyfillIncludes()
  {
    // https://tc39.github.io/ecma262/#sec-array.prototype.includes
    if ( !Array.prototype.includes )
    {
      Object.defineProperty( Array.prototype, 'includes',
      {
        value: function( searchElement, fromIndex )
        {

          if ( this === null )
          {
            throw new TypeError( '"this" is null or not defined' );
          }

          // 1. Let O be ? ToObject(this value).
          const o = Object( this );

          // 2. Let len be ? ToLength(? Get(O, "length")).
          const len = o.length >>> 0;

          // 3. If len is 0, return false.
          if ( len === 0 )
          {
            return false;
          }

          // 4. Let n be ? ToInteger(fromIndex).
          //    (If fromIndex is undefined, this step produces the value 0.)
          const n = fromIndex | 0;

          // 5. If n ≥ 0, then
          //  a. Let k be n.
          // 6. Else n < 0,
          //  a. Let k be len + n.
          //  b. If k < 0, let k be 0.
          let k;
          k = Math.max( n >= 0 ? n : len - Math.abs( n ), 0 );

          function sameValueZero( x, y )
          {
            return x === y || ( typeof x === 'number' && typeof y === 'number' && isNaN( x ) && isNaN( y ) );
          }

          // 7. Repeat, while k < len
          while ( k < len )
          {
            // a. Let elementK be the result of ? Get(O, ! ToString(k)).
            // b. If SameValueZero(searchElement, elementK) is true, return true.
            if ( sameValueZero( o[ k ], searchElement ) )
            {
              return true;
            }
            // c. Increase k by 1.
            k++;
          }

          // 8. Return false
          return false;
        }
      } );
    }
  }
}